import { FC } from 'react';
import { Avatar, Col, Row, Space, Typography } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { CommentComposer, CommentMessage } from '@/shared';
import type { CommentComposerValue, ResourceComment } from '@/types';
import { ellipsisStyling, formatDate } from '@/utilities';

import { commenterName, MemoCommentHoverCardPosition, metadataTarget } from './memoCommentUtils';

interface MemoCommentHoverCardProps {
    canComment?: boolean;
    comment: ResourceComment;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onReplySubmit: (value: CommentComposerValue) => Promise<boolean | void> | boolean | void;
    position: MemoCommentHoverCardPosition;
    replies: ResourceComment[];
    replySubmitting?: boolean;
    resourceId?: string;
}

const MemoCommentHoverCard: FC<MemoCommentHoverCardProps> = ({
    canComment,
    comment,
    onMouseEnter,
    onMouseLeave,
    onReplySubmit,
    position,
    replies,
    replySubmitting,
    resourceId,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const target = metadataTarget(comment);
    const quote = typeof target.quote === 'string' ? target.quote : undefined;
    const user = comment.user ?? comment.member?.user;

    return (
        <div
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            style={{
                background: token.colorBgElevated,
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadiusLG,
                boxShadow: token.boxShadowSecondary,
                left: position.left,
                maxHeight: position.maxHeight,
                overflow: 'auto',
                padding: '0.875rem',
                position: 'fixed',
                top: position.top,
                width: position.width,
                zIndex: 1050,
            }}
        >
            <Space direction='vertical' size={10} style={{ width: '100%' }}>
                {quote && (
                    <Typography.Text
                        style={{
                            borderLeft: `3px solid ${token.colorBorder}`,
                            color: token.colorTextSecondary,
                            display: 'block',
                            paddingLeft: '0.625rem',
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        {quote}
                    </Typography.Text>
                )}
                <Row align='top' gutter={8} wrap={false}>
                    <Col flex='none'>
                        <Avatar src={user?.avatar} size={24}>
                            {commenterName(comment).charAt(0)}
                        </Avatar>
                    </Col>
                    <Col flex='auto' style={{ minWidth: 0 }}>
                        <Row align='middle' gutter={6} wrap={false}>
                            <Col flex='auto' style={{ minWidth: 0 }}>
                                <Typography.Text style={{ ...ellipsisStyling, display: 'block' }}>
                                    {commenterName(comment)}
                                </Typography.Text>
                            </Col>
                            <Col flex='none'>
                                <small style={{ color: token.colorTextTertiary }}>
                                    {formatDate(comment.createdAt, 'mini')}
                                </small>
                            </Col>
                        </Row>
                        <Typography.Paragraph style={{ marginBottom: 0 }}>
                            <CommentMessage message={comment.message} metadata={comment.metadata} />
                        </Typography.Paragraph>
                    </Col>
                </Row>
                {replies.length > 0 && (
                    <Space
                        direction='vertical'
                        size={8}
                        style={{
                            borderTop: `1px solid ${token.colorBorderSecondary}`,
                            paddingTop: '0.625rem',
                            width: '100%',
                        }}
                    >
                        {replies.map((reply) => {
                            const replyUser = reply.user ?? reply.member?.user;

                            return (
                                <Row key={reply.id} align='top' gutter={8} wrap={false}>
                                    <Col flex='none'>
                                        <Avatar src={replyUser?.avatar} size={20}>
                                            {commenterName(reply).charAt(0)}
                                        </Avatar>
                                    </Col>
                                    <Col flex='auto' style={{ minWidth: 0 }}>
                                        <Row align='middle' gutter={6} wrap={false}>
                                            <Col flex='auto' style={{ minWidth: 0 }}>
                                                <Typography.Text
                                                    style={{
                                                        ...ellipsisStyling,
                                                        display: 'block',
                                                        fontSize: token.fontSizeSM,
                                                    }}
                                                >
                                                    {commenterName(reply)}
                                                </Typography.Text>
                                            </Col>
                                            <Col flex='none'>
                                                <small style={{ color: token.colorTextTertiary }}>
                                                    {formatDate(reply.createdAt, 'mini')}
                                                </small>
                                            </Col>
                                        </Row>
                                        <Typography.Paragraph
                                            style={{
                                                color: token.colorTextSecondary,
                                                marginBottom: 0,
                                                whiteSpace: 'pre-wrap',
                                            }}
                                        >
                                            <CommentMessage message={reply.message} metadata={reply.metadata} />
                                        </Typography.Paragraph>
                                    </Col>
                                </Row>
                            );
                        })}
                    </Space>
                )}
                {resourceId && (
                    <CommentComposer
                        disabled={!canComment}
                        maxHeight='6rem'
                        minHeight='2.25rem'
                        onSubmit={onReplySubmit}
                        placeholder='Reply...'
                        resourceId={resourceId}
                        resourceType='memos'
                        submitting={replySubmitting}
                    />
                )}
            </Space>
        </div>
    );
};

export default MemoCommentHoverCard;
